/**
 * Settings ▸ Roles & Org route (ADR-0030).
 *
 * The dynamic role-management surface that supersedes ADR-0011's read-only
 * capability matrix: a role editor (permission matrix grouped by area) + the
 * company department tree. Authorisation is server-enforced
 * (`permissionGate('roles.manage' | 'settings.manage')`); the `canManage`
 * booleans here just hide affordances for members without the grant.
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import { meQueryOptions } from "../../../../lib/session.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell } from "../../../../features/settings/index.ts";
import { RolesSection, DepartmentsSection } from "../../../../features/settings/roles/index.ts";

export const Route = createFileRoute("/(workspace)/settings/roles/")({
  // URL-level `roles.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "roles.manage")) {
      throw notFound();
    }
  },
  component: RolesPage,
});

function RolesPage() {
  // Granular permission checks — the legacy `role === "Founder"` gate hid
  // manage affordances from Admin/HR users who legitimately hold
  // `roles.manage` and/or `settings.manage`. Server enforcement is still
  // via `permissionGate` in `roles.ts` / `departments.ts`.
  const canManageRoles = usePermission("roles.manage");
  const canManageDepartments = usePermission("settings.manage");

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Roles & org" }]}>
      <SettingsShell
        activeSection="roles"
        pageTitle="Roles & org"
        pageDescription="Define what each role can do and how your organisation is structured. Built-in roles cover the common garment-factory functions; create your own for anything specific."
      >
        <RolesSection canManage={canManageRoles} />
        <DepartmentsSection canManage={canManageDepartments} />
      </SettingsShell>
    </WorkspaceShell>
  );
}
